import React, { FC, useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { WalletStyles } from '../styles';
import { listUnspent, broadcast, getTransactionRaw } from '../../../webservice/mProviders';
import { homenetwork, homepvtKey, compState } from '../../../Headerr';
import { bsv } from '../../../scrypt-ts-this/distthis/index';

interface OrdinalItem {
  txid: string;
  vout: number;
  satoshis: number;
  isToken?: boolean;
  title?: string;
  author?: string;
  hash?: string;
}

const WalletOrdinals: FC = () => {
  const [ordinals, setOrdinals] = useState<OrdinalItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [selectedOrdinal, setSelectedOrdinal] = useState<OrdinalItem | null>(null);
  const [recipientAddress, setRecipientAddress] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [transferResult, setTransferResult] = useState('');

  const fetchOrdinals = useCallback(async () => {
    if (homepvtKey.length !== 64) return;
    setLoading(true);
    try {
      const privateKey = bsv.PrivateKey.fromHex(homepvtKey, homenetwork);
      const addr = bsv.Address.fromPrivateKey(privateKey).toString();
      const UTXOs = await listUnspent(addr, homenetwork);

      // Filter 1-sat UTXOs as potential Ordinals
      const onesat = UTXOs.filter((u: any) => u.satoshis === 1);
      const parsedOrdinals: OrdinalItem[] = [];

      for (const u of onesat) {
        let isToken = false;
        let title, author, hash;
        try {
          const rawTxHex = await getTransactionRaw(u.txId, homenetwork);
          if (rawTxHex) {
            const tx = new bsv.Transaction(rawTxHex);
            const scriptHex = tx.outputs[u.outputIndex].script.toHex();
            
            // Check if it ends with OP_DROP (75)
            if (scriptHex.endsWith('75')) {
              // Find OP_CHECKSIG (ac)
              const checkSigIndex = scriptHex.lastIndexOf('ac');
              if (checkSigIndex !== -1) {
                const payloadHex = scriptHex.substring(checkSigIndex + 2, scriptHex.length - 2);
                let utf8 = '';
                for (let i = 0; i < payloadHex.length; i += 2) {
                  const charCode = parseInt(payloadHex.substr(i, 2), 16);
                  if (charCode) utf8 += String.fromCharCode(charCode);
                }
                
                try {
                  utf8 = decodeURIComponent(escape(utf8));
                } catch (e) {
                  // Ignore
                }

                const jsonStart = utf8.indexOf('{');
                const jsonEnd = utf8.lastIndexOf('}');
                if (jsonStart !== -1 && jsonEnd !== -1) {
                  const jsonStr = utf8.substring(jsonStart, jsonEnd + 1);
                  const parsed = JSON.parse(jsonStr);
                  if (parsed.action === 'REGISTER_IP') {
                    isToken = true;
                    title = parsed.title;
                    author = parsed.author;
                    hash = parsed.hash;
                  }
                }
              }
            }
          }
        } catch (e) {
          console.error('Error parsing token for txid', u.txId, e);
        }

        parsedOrdinals.push({
          txid: u.txId,
          vout: u.outputIndex,
          satoshis: u.satoshis,
          isToken,
          title,
          author,
          hash
        });
      }

      setOrdinals(parsedOrdinals);
    } catch (e) {
      console.error('Failed to fetch ordinals:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrdinals();
  }, [fetchOrdinals]);

  const handleTransferClick = (ordinal: OrdinalItem) => {
    setSelectedOrdinal(ordinal);
    setTransferOpen(true);
    setRecipientAddress('');
    setTransferResult('');
  };

  const handleTransfer = async () => {
    if (!selectedOrdinal || !recipientAddress) return;
    setTransferring(true);
    try {
      const privateKey = bsv.PrivateKey.fromHex(homepvtKey, homenetwork);
      privateKey.compAdd(compState);
      const fromAddress = bsv.Address.fromPrivateKey(privateKey).toString();

      // Get all UTXOs for fees
      const UTXOs = await listUnspent(fromAddress, homenetwork);
      const feeUtxos = UTXOs.filter((u: any) => u.satoshis > 1);

      const tx = new bsv.Transaction();

      // Add ordinal UTXO first (index 0)
      tx.from({
        txId: selectedOrdinal.txid,
        outputIndex: selectedOrdinal.vout,
        script: bsv.Script.buildPublicKeyHashOut(bsv.Address.fromString(fromAddress)).toHex(),
        satoshis: selectedOrdinal.satoshis,
      });

      // Add fee UTXOs
      let feeTotal = 0;
      for (const u of feeUtxos) {
        tx.from(u);
        feeTotal += u.satoshis;
        if (feeTotal >= 1000) break;
      }

      // Send ordinal to recipient (1 sat)
      tx.to(recipientAddress, 1);
      tx.change(fromAddress);
      tx.feePerKb(500);
      tx.sign(privateKey);

      const txid = await broadcast(tx, homenetwork);
      if (txid) {
        setTransferResult(`✅ Transferido! TXID: ${txid}`);
        fetchOrdinals();
      } else {
        setTransferResult('❌ Falha na transferência.');
      }
    } catch (e: any) {
      setTransferResult(`❌ Erro: ${e.message}`);
    } finally {
      setTransferring(false);
    }
  };

  const truncateTxid = (txid: string) => {
    if (!txid || txid.length < 16) return txid;
    return `${txid.substring(0, 8)}...${txid.substring(txid.length - 8)}`;
  };

  return (
    <Box>
      <Box sx={WalletStyles.card}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={WalletStyles.sectionTitle}>Meus Ordinals / NFTs</Typography>
          <Button variant="outlined" size="small" onClick={fetchOrdinals} sx={WalletStyles.outlinedButton}>
            🔄 Atualizar
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ color: '#7BA238' }} />
          </Box>
        ) : ordinals.length === 0 ? (
          <Typography sx={WalletStyles.noDataText}>
            Nenhum Ordinal (1-sat UTXO) encontrado neste endereço.
          </Typography>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
            {ordinals.map((ord, idx) => (
              <Box key={idx} sx={{
                ...WalletStyles.card,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                '&:hover': { borderColor: '#7BA238' },
              }}>
                <Typography variant="body2" sx={{ fontSize: '2rem' }}>
                  {ord.isToken ? '📜' : '🖼️'}
                </Typography>
                
                {ord.isToken ? (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#7BA238', textAlign: 'center' }}>
                      {ord.title || 'Propriedade Intelectual'}
                    </Typography>
                    {ord.author && (
                      <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                        Por: {ord.author}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#7BA238', textAlign: 'center' }}>
                    {truncateTxid(ord.txid)}
                  </Typography>
                )}

                <Typography variant="body2" sx={{ color: 'rgba(123, 162, 56, 0.6)', fontSize: '0.7rem' }}>
                  vout: {ord.vout} | {ord.satoshis} sat
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleTransferClick(ord)}
                  sx={{ ...WalletStyles.outlinedButton, fontSize: '0.7rem' }}
                >
                  Transferir
                </Button>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Dialog open={transferOpen} onClose={() => setTransferOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#7BA238' }}>Transferir Ordinal</DialogTitle>
        <DialogContent>
          {selectedOrdinal && (
            <Typography variant="body2" sx={{ mb: 2, fontFamily: 'monospace', fontSize: '0.8rem' }}>
              TXID: {selectedOrdinal.txid}
            </Typography>
          )}
          <TextField
            label="Endereço Destino"
            value={recipientAddress}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setRecipientAddress(e.target.value)}
            fullWidth
            variant="outlined"
            sx={{ ...WalletStyles.inputField, mt: 1 }}
          />
          {transferResult && (
            <Typography variant="body2" sx={{ mt: 2, wordBreak: 'break-all', color: transferResult.startsWith('✅') ? '#7BA238' : '#f44336' }}>
              {transferResult}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferOpen(false)}>Cancelar</Button>
          <Button onClick={handleTransfer} disabled={transferring} variant="contained" sx={WalletStyles.actionButton}>
            {transferring ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Transferir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WalletOrdinals;
